import type { Domain } from '@/types/domain';

// GA4 Measurement ID - This should be moved to environment variables
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

// Initialize GA4
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Initialize GA4
export const initializeGA = () => {
  // Only initialize GA in production
  if (import.meta.env.PROD && typeof window !== 'undefined' && GA_MEASUREMENT_ID) {
    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false, // We'll handle page views manually
    });
  }
};

// Track page view with domain context
export const trackPageView = (domain: Domain, path: string, title: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_title: title,
    page_location: `https://${domain.hostname}${path}`,
    page_path: path,
    domain_id: domain.id,
    domain_name: domain.name,
  });
};

// Track custom events
export const trackEvent = (
  eventName: string,
  eventParams: {
    domain_id?: string;
    domain_name?: string;
    [key: string]: any;
  }
) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, eventParams);
};

// Common event tracking functions
export const trackArticleView = (
  domain: Domain | null,
  articleId: string,
  articleTitle: string,
  category?: string
) => {
  if (!domain) return;
  
  trackEvent('article_view', {
    domain_id: domain.id,
    domain_name: domain.name,
    article_id: articleId,
    article_title: articleTitle,
    category: category,
  });
};

export const trackNewsletterSignup = (domain: Domain, email: string) => {
  trackEvent('newsletter_signup', {
    domain_id: domain.id,
    domain_name: domain.name,
    email_domain: email.split('@')[1], // Only track email domain for privacy
  });
};

export const trackCategoryView = (domain: Domain, category: string) => {
  trackEvent('category_view', {
    domain_id: domain.id,
    domain_name: domain.name,
    category: category,
  });
};

export const trackSearch = (domain: Domain, searchTerm: string, resultsCount: number) => {
  trackEvent('search', {
    domain_id: domain.id,
    domain_name: domain.name,
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

export const trackShare = (
  domain: Domain,
  articleId: string,
  articleTitle: string,
  platform: string
) => {
  trackEvent('share', {
    domain_id: domain.id,
    domain_name: domain.name,
    article_id: articleId,
    article_title: articleTitle,
    platform: platform,
  });
}; 